/**
 * The AI usage type that a spending limit applies to.
 *
 * Mirrors AiUsageType in the supplier management API. Only IMAGE_EDITING reaches the UI
 * today; the other members exist because the read returns every row of the supplier, and
 * because shortening and translation move onto spending limits later.
 */
export enum AiUsageType {
    UNKNOWN = 'UNKNOWN',
    SHORTEN = 'SHORTEN',
    TRANSLATE = 'TRANSLATE',
    IMAGE_EDITING = 'IMAGE_EDITING',
}

// The transports disagree on how to spell a usage: GraphQL answers with the constant-case
// name, REST answers with the number and names the row in the path by its backend name.
// This table holds the three spellings, so nothing else has to know there is more than one.
const AI_USAGE_TYPE_WIRE_FORMS: Record<AiUsageType, { code: number; route: string }> = {
    [AiUsageType.UNKNOWN]: { code: 0, route: 'unknown' },
    [AiUsageType.SHORTEN]: { code: 1, route: 'shorten' },
    [AiUsageType.TRANSLATE]: { code: 2, route: 'translate' },
    [AiUsageType.IMAGE_EDITING]: { code: 3, route: 'imageEditing' },
};

export function getAiUsageTypeRoute(usage: AiUsageType): string {
    return AI_USAGE_TYPE_WIRE_FORMS[usage].route;
}

export function getAiUsageTypeFromCode(code: number): AiUsageType {
    const usages = Object.keys(AI_USAGE_TYPE_WIRE_FORMS) as AiUsageType[];

    // An unrecognised code reads as UNKNOWN, which every caller must already refuse.
    return usages.find((usage) => AI_USAGE_TYPE_WIRE_FORMS[usage].code === code) ?? AiUsageType.UNKNOWN;
}

// The row, as both the GraphQL read and the mapped REST read present it.
export interface SupplierSpendingLimit {
    usage: AiUsageType;
    limitAmount: number;
    isUnlimited: boolean;
    markupPercentage: number;
    isActive: boolean;
}

// The row as the REST surface puts it on the wire, before the service maps the usage.
export interface SupplierSpendingLimitResponse {
    supplierId: string;
    usage: number;
    limitAmount: number;
    isUnlimited: boolean;
    markupPercentage: number;
    isActive: boolean;
}

export interface SetSupplierSpendingLimitRequest {
    limitAmount: number;
    isUnlimited: boolean;
    markupPercentage: number;
    isActive: boolean;
}
